import { styled } from '@linaria/react';
import type { ReactNode } from 'react';

interface SettingRowProps {
  /** Id for the label, so the control can point `aria-labelledby` at it. */
  labelId: string;
  label: string;
  description?: ReactNode;
  /** Id for the description, so the control can point `aria-describedby` at it. */
  descriptionId?: string;
  /** The control: a switch, select, or segmented control. */
  control: ReactNode;
}

/**
 * Renders one setting as a label and description on the left and its control
 * on the right; wraps to a stacked layout on narrow screens.
 *
 * @param {SettingRowProps} props - The label, description, and control
 * @returns {JSX.Element} The setting row
 *
 * @example
 * <SettingRow
 *   labelId="history-label"
 *   label="Reference chat history"
 *   description="Let Flora use your past chats as context."
 *   control={<Switch on={on} onToggle={setOn} ariaLabel="Reference chat history" />}
 * />
 */
export function SettingRow({ labelId, label, description, descriptionId, control }: SettingRowProps) {
  return (
    <Row>
      <Text>
        <Label id={labelId}>{label}</Label>
        {description && <Description id={descriptionId}>{description}</Description>}
      </Text>
      <Control>{control}</Control>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 24px;

  & + & {
    border-top: 1px solid var(--sky-light);
  }
`;

const Text = styled.div`
  flex: 1 1 280px;
  min-width: 0;
`;

const Label = styled.div`
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  line-height: 1.25rem;
  color: var(--ink-darkest);
`;

const Description = styled.div`
  margin-top: 2px;
  font-size: var(--font-size-small);
  line-height: 1.125rem;
  color: var(--ink-light);
`;

const Control = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export type { SettingRowProps };
